import * as is from 'is';
import dynamicImportWrapper from './dynamic-import-wrapper';
import { ValueError } from './errors';

/**
 * Checks if the window object is defined.
 *
 * @returns {boolean}
 */
export const isBrowser = () => typeof window !== 'undefined';

/**
 * Checks if the window object has the fetch function.
 *
 * @returns {boolean}
 */
export const hasFetch = () => isBrowser() && is.function(window?.fetch);

/**
 * Indicates if the site is being executed as a child / in an iframe.
 *
 * @returns {boolean}
 */
export function isMicroapp() {
  if (!isBrowser()) {
    return false;
  }

  if (window.parent === window) {
    return false;
  }

  return window.parent.location.origin === window.location.origin;
}

/**
 * Returns an object to be used as a logging object.
 *
 * @param {string} [label = 'Main'] A string to display to make the log message unique. Defaults to 'Main'.
 * @returns {{debug(), info(), warn(), error()}}
 */
export const getLogger = (label = 'Main') => {
  return {
    debug(...args) { console.debug(new Date().toISOString(), Date.now(), `[${label}]`, ...args); },
    info(...args) { console.info(new Date().toISOString(), Date.now(), `[${label}]`, ...args); },
    warn(...args) { console.warn(new Date().toISOString(), Date.now(), `[${label}]`, ...args); },
    error(...args) { console.error(new Date().toISOString(), Date.now(), `[${label}]`, ...args); },
  };
};

export const log = getLogger();

/**
 * Create a MicroappMessenger class for simple communication with the
 * Shell application.
 *
 * @returns MicroappMessenger instance provided by the Platform Gateway.
 */
export async function generateMicroappMessenger() {
  try {
    if (isMicroapp()) {
      const { MicroappMessenger } = await dynamicImportWrapper('/assets/microapp-tools.bundle.js');
      if (!MicroappMessenger) {
        console.warn('Unable to load the tools necessary for certain features. Please reload and try again.');
        return;
      }

      return new MicroappMessenger();
    }
  } catch (error) { log.debug(error); }
}

/**
 * Creates a plain object with the keys: path and displayName. If the icon property is provided, then it will be
 * added to the returned object.
 *
 * @param {string} path The name of the path.
 * @param {*} options An options object to provided. Defaults to {}
 * @param {string} [options.displayName = [path capitalized]] The name to display. Defaults to the value of the path
 * argument, capitalized.
 * @param {string} [options.icon] The font-awesome icon to display next to the display name. Value is not returned if
 * it is not provided.
 * @returns {{path, displayName} | {path, displayName, icon}}
 */
export function createNavEntry(path, options = {}) {
  if (!is.string(path)) {
    throw new TypeError('path argument must be a string.');
  }
  if (is.empty(path)) {
    throw new ValueError('path argument cannot be empty.');
  }
  const {
    displayName = capitalizeWords(path),
    icon,
  } = options ?? {};
  return is.string(icon)
    // eslint-disable-next-line object-curly-newline
    ? { path, displayName, icon }
    : { path, displayName };
}

/**
 * Converts string to camelCase
 * @param {*} str
 * @returns
 */
export const toCamelCase = (str) => {
  return str
    .replace(/[^a-zA-Z0-9 ]+/g, ' ') // Replace non-alphanumeric characters with space
    .toLowerCase()
    .split(' ')
    .filter(Boolean) // Remove empty strings
    .map((word, index) => index === 0
      ? word
      : word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

/**
 * Capitalizes the first letter in the word
 * @param {*} word to be capitalized
 * @returns
 */
export const capitalizeWord = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * Capitalizes the first letter in each word of a space separated phrase
 * @param {*} phrase to be capitalized
 * @returns
 */
export const capitalizeWords = (phrase) =>
  phrase.split(' ').map((word) => capitalizeWord(word)).join(' ');

/**
 * Special Cases where camel case to header has special characters
 */
const specialCases = {
  onlineOfflineAStream: 'Online/Offline a Stream',
};

/**
 * Converts camelCase to Header
 * Ex: streamProfile --> Stream Profile
 * @param {*} camelCaseStr
 * @returns
 */
export const camelToHeader = (camelCaseStr) => {
  if (camelCaseStr) {
    if (specialCases[camelCaseStr]) {
      return specialCases[camelCaseStr];
    }

    return camelCaseStr
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, function (match) { return match.toUpperCase(); })
      .trim();
  }
};

/**
 * Converts Header to kebab-case
 * Ex: Stream Profile --> stream-profile
 * @param {*} headerStr
 * @returns
 */
export const headerToKebab = (headerStr) => {
  if (headerStr) {
    return headerStr.toLowerCase().replace(/\s+/g, '-');
  }
};

export const headerToSnakeCase = (headerStr) => {
  if (headerStr) {
    return headerStr.toLowerCase().replace(/\s+/g, '_');
  }
};

/**
 * Converts kebab-case to Header
 * Ex: API-key --> API Key
 * @param {*} kebabStr
 * @returns
 */
export const kebabToHeader = (kebabStr) => {
  if (kebabStr) {
    return kebabStr
      .replace(/-/g, ' ')
      .replace(/^./, (match) => match.toUpperCase())
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }
};

/**
 * Iterates over all keys in an object and returns a new object excluding null values
 * Ex: {name: 'Stream Manager', description: null} --> {name: 'Stream Manager'}
 * @param {*} obj
 * @returns
 */
export const filterEmptyValues = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) =>
      value !== null && value !== undefined && value !== '' && value !== 0
    )
  );
};

/**
 * Iterates over an object and capitalizes the keys in that object
 * Ex: {jurisdiction: 'test'} --> {Jurisdiction: 'test'}
 * @param {*} obj
 * @returns
 */
export const capitalizeKeys = (obj) => {
  if (!obj) return {};

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [capitalizeWord(key), value])
  );
};

/**
 * If a value is an empty string, it will make it null.
 * Otherwise, it will just return the value.
 * @param {*} value to check
 * @returns
 */
export const assignValue = (value) => is.empty(value?.toString()) ? null : value;

/**
 * Formats an array of query parameters into a query string.
 *
 * This function takes an array of query parameter objects, where each object contains
 * a `name` and a `value` property. It concatenates these parameters into a query string
 * that starts with a `?` character.
 *
 * @param {Array} queryParams - An array of objects representing the query parameters.
 * Each object must have the following structure:
 *   - `name` (string): The name of the query parameter.
 *   - `value` (string | number): The value of the query parameter.
 *
 * @returns {string} The formatted query string. If the input array is empty, an empty string is returned.
 *
 * @example
 * const params = [
 *   { name: 'id', value: 123 },
 *   { name: 'type', value: 'camera' }
 * ];
 * const queryString = formatParams(params);
 * console.log(queryString); // Output: "?id=123?type=camera"
 */
export const formatParams = (queryParams) => {
  if (!queryParams.length) return '';
  let queryString = '';
  queryParams.forEach((param) => {
    queryString += `?${param.name}=${param.value}`;
  });

  return queryString;
};

/**
 * Determines the current user privilege mode (admin/operator) based on environment variables and test flags.
 *
 * - In test mode (when VITE_TEST_MODE is 'true'), the value of window.userIsAdmin is used.
 * - Otherwise, the value of VITE_DEV_ROLE is checked (admin if 'admin').
 *
 * This function is used to restrict or grant admin privileges in development and automated test environments only.
 *
 * @param {object} [env] - Optional environment object (for testing).
 * @param {object} [win] - Optional window object (for testing).
 * @returns {boolean} True if the user is considered admin, false otherwise.
 */
export const isDevOrTestMode = (env = import.meta.env, win = window) => {
  const isDevelopmentMode = env.VITE_DEV_ROLE === 'admin';
  const isTestMode =
    env.VITE_TEST_MODE === 'true' &&
    (win.userIsAdmin === true || win.userIsAdmin === false);
  return isTestMode ? win.userIsAdmin : isDevelopmentMode;
};

// As of 12-17-25, this endpoint was added to Gateway for the sole purpose of providing the
// admin status of the current user to Manager UI to restrict operator actions. If the need
// for this functionality ever expands or drastically changes, a possible alternative that
// was discussed would be to send the admin status data via the shell messenger.
export const isAdmin = async () => {
  try {
    const response = await fetch('/api/loggedInUserIsAdmin');
    const isAdmin = await response.json();
    return isAdmin;
  } catch (e) {
    console.error(`Error while retrieving user's admin status: ${e}`);
    return false;
  }
};
